<template>
    <div class="p-0">
        <template v-if="notProtected">
            <div class="mb-4">
                <div class="font-size-4 mb-2">
                    {{ userTypeSubHeader }}
                </div>
                <div
                    v-if="postDisclosureSellerPage"
                    class="info-text mb-4"
                >
                    <div class="font-size-4 mb-2">
                        <span class="fa fa-exclamation-triangle text-yellow float-left"/> &nbsp; <b>{{
                            currentUserName
                        }}</b> , as a home seller, you are open to some risk in the sale of your home.
                    </div>

                    <div class="font-size-4 mb-2">
                        <b>Remember: </b> Most lawsuits are filed 10-13 months after the sale and can cost some sellers
                        $15,000+ in legal fees, even if it is a false or frivolous claim.
                    </div>
                </div>

                <div class="font-size-4 font-weight-bold">
                    Which Home Sale Legal Protection™ package would you like to purchase?
                </div>
            </div>

            <div class="mb-4">
                <hslp-form
                    ref="hslpForm"
                    :is-upsell="!isAgent"
                    :no-protection="false"
                    :order-app-resource="orderResource"
                    :upsell-payment-visible="upsellPaymentVisible"
                    @change-payment-method="handleChangePaymentMethod"
                    stripe-version="v1"
                />
            </div>

            <b-row class="mb-4">
                <b-col cols="12">
                    <p class="info-text">
                        By submitting this form, you verify that the home sale has not yet closed, and confirm that the
                        property is not a "flip" transaction under which a seller is selling an investment property
                        owned for only a short period of time, nor is it a sale by a builder of new construction.
                        <b-link
                            href="https://sellersshield.com/terms"
                            target="_blank"
                        >
                            Terms & Conditions
                        </b-link>
                    </p>
                </b-col>

            </b-row>

            <b-form-row class="justify-content-between">
                <b-col
                    class="mb-2"
                    cols="12"
                    lg="4"
                    md="6"
                >
                    <b-button
                        class="simple-button"
                        size="lg"
                        variant="tertiary"
                        @click="$bvModal.hide('hslp-modal')"
                    >
                        Not Now
                    </b-button>
                </b-col>
                <b-col
                    class="mb-2"
                    cols="12"
                    lg="4"
                    md="6"
                >
                    <b-button
                        block
                        size="lg"
                        variant="primary"
                        @click="addProtection"
                    >
                        <b-spinner
                            v-if="saving"
                            class="mr-1"
                            small
                        />

                        Add Protection
                    </b-button>
                </b-col>
            </b-form-row>
        </template>

        <template v-else>
            <b-row
                align-h="center"
                class="mb-4"
            >
                <b-col cols="2">
                    <b-img
                        :src="require('@assets/images/icon-protected.png')"
                        alt="A proud, alert English bulldog"
                        fluid
                    />
                </b-col>
            </b-row>

            <div class="mb-5">
                <div v-if="payAtClose">
                    <h2 class="text-center mb-4">
                        Thank you! We've reserved protection for {{ isAgent ? primarySellerFirstName : 'you' }}.
                    </h2>

                    <div
                        v-if="isAgent"
                        class="font-size-4 text-center mb-2"
                    >
                        We just sent {{ primarySellerFirstName }} an email with details about your purchase.
                    </div>
                    <div class="font-size-4 text-center">
                        {{ isAgent ? primarySellerFirstName : 'You' }} will receive a secure certificate of Home Sale
                        Legal
                        Protection™ once payment has been received from the title company
                        {{ isAgent ? 'out of commission proceeds' : 'after closing' }}.
                    </div>
                </div>

                <div v-else>
                    <h2 class="text-center mb-4">
                        Your purchase is complete!
                    </h2>

                    <div class="font-size-4 text-center">
                        We've sent a secure certificate of Home Sale Legal Protection™ to
                        {{ isAgent ? `${primarySellerFirstName}'s` : 'your' }} email box.
                    </div>
                </div>
            </div>

            <b-form-row class="justify-content-end">
                <b-col cols="4">
                    <b-button
                        block
                        size="lg"
                        variant="primary"
                        @click="$bvModal.hide('hslp-modal')"
                    >
                        Close
                    </b-button>
                </b-col>
            </b-form-row>
        </template>
    </div>
</template>

<script>
import FormErrorsMixin from '@sellers-shield-home-protection/ss-ui-kit/src/components/FormErrors/FormErrorsMixin';
import UserMixin from '@mixins/UserMixin';
import HslpForm from '../../HslpForm/HslpForm';
import {invitationPrimarySeller, notProtected, payAtClose, primarySeller} from '@helpers/order';
import {pageId} from "@helpers/fromPage";

export default {
    name: 'HslpModalV1',
    components: {
        HslpForm
    },
    mixins: [
        FormErrorsMixin,
        UserMixin,
    ],
    props: {
        orderAppResource: {
            type: Object,
            required: true,
            default: null,
        },
    },
    data: () => ({
        saving: false,
        selectedPaymentMethod: 'pay-now',
        upsellPaymentVisible: false,
    }),
    computed: {
        orderResource() {
            return this.orderAppResource || {};
        },
        primarySellerFirstName() {
            return primarySeller(this.orderResource).firstName || invitationPrimarySeller(this.orderResource).firstName;
        },
        notProtected() {
            return notProtected(this.orderResource);
        },
        payAtClose() {
            return payAtClose(this.orderResource);
        },
        currentPageId() {
            return pageId(this.$route.name, this.isAgent);
        },
        postDisclosureSellerPage() {
            if (!this.isSeller) {
                return false;
            }
            return this.$route.name === 'order-disclosure-form';
        },
        userTypeSubHeader() {
            if (this.isSeller) {
                return 'Don\'t just be a smart seller, be a protected seller.';
            }
            if (this.isAgent) {
                return 'Security for your client after the sale.';
            }
        },
        currentUserName() {
            return this.me.firstName;
        },
    },
    methods: {
        async handleChangePaymentMethod(selectedPaymentMethod) {
            this.selectedPaymentMethod = selectedPaymentMethod;
            this.upsellPaymentVisible = true;
        },
        async addProtection() {
            await this.catchAppResourceErrors(async () => {
                const orderAppResource = await this.$refs.hslpForm.addProtection(this.currentPageId);

                await this.$emit('fetchOrder', orderAppResource.uuid);
            }, 'saving');
        },
    },
}
</script>
